import type { Document } from './document.ts';
import type { NodeData, NodeId, Rect, Vec2 } from './types.ts';
import { apply, invert, flattenPath, nodeOutline, pointInPolylines, distToSegment, intersects, containsRect, transformRect } from './geometry.ts';
import { connectorGeometry } from './layout.ts';

export interface HitOptions {
  /** Hit tolerance in page units (e.g. 4 / zoom). */
  tolerance?: number;
  includeLocked?: boolean;
}

function hasVisibleFill(n: NodeData) {
  return n.fills.some((f) => f.visible !== false);
}

/** Does page-space point p hit node n itself (ignoring children)? */
export function hitsNode(doc: Document, n: NodeData, p: Vec2, tol = 0): boolean {
  if (n.type === 'CONNECTOR') {
    const g = connectorGeometry(doc, n);
    const pts = g.kind === 'CURVED' ? flattenPath([{ c: 'M', ...g.points[0] }, { c: 'C', x1: g.points[1].x, y1: g.points[1].y, x2: g.points[2].x, y2: g.points[2].y, x: g.points[3].x, y: g.points[3].y }], 2)[0].points : g.points;
    const r = Math.max(tol, n.strokeWeight / 2 + 2);
    for (let i = 0; i + 1 < pts.length; i++) if (distToSegment(p, pts[i], pts[i + 1]) <= r) return true;
    return false;
  }
  const local = apply(invert(doc.worldTransform(n.id)), p);
  const inBox = local.x >= -tol && local.y >= -tol && local.x <= n.width + tol && local.y <= n.height + tol;
  switch (n.type) {
    case 'TEXT':
    case 'STICKY':
    case 'IMAGE':
    case 'SECTION':
      return inBox;
    case 'FRAME':
    case 'COMPONENT':
    case 'INSTANCE':
    case 'RECTANGLE':
      return inBox;
    case 'GROUP':
      return false; // groups are hit through their children
    case 'ELLIPSE': {
      const rx = n.width / 2 + tol,
        ry = n.height / 2 + tol;
      const dx = local.x - n.width / 2,
        dy = local.y - n.height / 2;
      return (dx * dx) / (rx * rx) + (dy * dy) / (ry * ry) <= 1;
    }
    case 'LINE':
      return distToSegment(local, { x: 0, y: 0 }, { x: n.width, y: 0 }) <= Math.max(tol, n.strokeWeight / 2 + 1);
    default: {
      if (!inBox && n.type !== 'VECTOR') return false;
      const outline = nodeOutline(n);
      if (!outline) return inBox;
      const polys = flattenPath(outline, 1);
      if ((hasVisibleFill(n) || n.type === 'SHAPE_WITH_TEXT') && pointInPolylines(local, polys, n.windingRule === 'EVENODD')) return true;
      const r = Math.max(tol, n.strokeWeight / 2 + 1);
      for (const poly of polys) {
        const pts = poly.points;
        for (let i = 0; i + 1 < pts.length; i++) if (distToSegment(local, pts[i], pts[i + 1]) <= r) return true;
        if (poly.closed && pts.length > 2 && distToSegment(local, pts[pts.length - 1], pts[0]) <= r) return true;
      }
      return false;
    }
  }
}

function clipRectOf(doc: Document, n: NodeData): Rect | null {
  if ((n.type === 'FRAME' || n.type === 'COMPONENT' || n.type === 'INSTANCE') && n.clipsContent) return doc.worldBounds(n.id);
  return null;
}

/**
 * Deepest-first hit test. Returns the path from the page's child down to the
 * deepest node under the point, or [] if nothing was hit.
 */
export function hitTestPath(doc: Document, pageId: NodeId, p: Vec2, opts: HitOptions = {}): NodeData[] {
  const tol = opts.tolerance ?? 0;
  const visit = (parentId: NodeId, trail: NodeData[]): NodeData[] | null => {
    const kids = doc.children(parentId);
    for (let i = kids.length - 1; i >= 0; i--) {
      const k = kids[i];
      if (!k.visible) continue;
      if (k.locked && !opts.includeLocked) continue;
      const clip = clipRectOf(doc, k);
      const insideClip = !clip || (p.x >= clip.x - tol && p.y >= clip.y - tol && p.x <= clip.x + clip.width + tol && p.y <= clip.y + clip.height + tol);
      if (insideClip && doc.children(k.id).length) {
        const deeper = visit(k.id, [...trail, k]);
        if (deeper) return deeper;
      }
      if (hitsNode(doc, k, p, tol)) return [...trail, k];
    }
    return null;
  };
  return visit(pageId, []) ?? [];
}

export function isContextContainer(doc: Document, n: NodeData): boolean {
  if (n.type === 'PAGE' || n.type === 'SECTION') return true;
  if (n.type === 'FRAME' || n.type === 'COMPONENT') {
    const p = doc.get(n.parent);
    return !!p && (p.type === 'PAGE' || p.type === 'SECTION');
  }
  return false;
}

/**
 * Pick the node a click should select, following design-tool conventions:
 * clicks select the top-most layer inside the nearest "context" container
 * (page, section, top-level frame). `scope` lets callers "enter" a group/frame.
 */
export function pickSelectable(doc: Document, path: NodeData[], scope?: NodeId | null): NodeData | undefined {
  if (!path.length) return undefined;
  if (scope) {
    const i = path.findIndex((n) => n.id === scope);
    if (i >= 0) return path[i + 1] ?? path[i];
  }
  // Walk down while the current node is a context container with a deeper hit.
  let i = 0;
  while (i < path.length - 1 && isContextContainer(doc, path[i])) i++;
  return path[i];
}

/** Nodes (direct children of `parentId`, recursing into context containers) touched by a marquee. */
export function nodesInRect(doc: Document, parentId: NodeId, r: Rect, mode: 'intersect' | 'contain' = 'intersect'): NodeData[] {
  const out: NodeData[] = [];
  for (const k of doc.children(parentId)) {
    if (!k.visible || k.locked) continue;
    const b = k.type === 'GROUP' ? groupBounds(doc, k) : doc.worldBounds(k.id);
    if (isContextContainer(doc, k) && k.type !== 'PAGE' && !containsRect(r, b) && intersects(r, b) && doc.children(k.id).length) {
      out.push(...nodesInRect(doc, k.id, r, mode));
      continue;
    }
    if (mode === 'contain' ? containsRect(r, b) : intersects(r, b)) out.push(k);
  }
  return out;
}

/** Point in polygon (even-odd). */
export function pointInPolygon(p: { x: number; y: number }, poly: { x: number; y: number }[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i],
      b = poly[j];
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y || 1e-12) + a.x) inside = !inside;
  }
  return inside;
}

/**
 * Lasso selection (Figma Draw): layers whose center lies inside the drawn path, or that sit
 * entirely inside it. Containers the lasso only partly covers are searched inside, like marquee.
 */
export function nodesInLasso(doc: Document, parentId: NodeId, poly: { x: number; y: number }[]): NodeData[] {
  if (poly.length < 3) return [];
  const out: NodeData[] = [];
  const corners = (b: Rect) => [
    { x: b.x, y: b.y },
    { x: b.x + b.width, y: b.y },
    { x: b.x + b.width, y: b.y + b.height },
    { x: b.x, y: b.y + b.height },
  ];
  for (const k of doc.children(parentId)) {
    if (!k.visible || k.locked) continue;
    const b = k.type === 'GROUP' ? groupBounds(doc, k) : doc.worldBounds(k.id);
    const all = corners(b).every((c) => pointInPolygon(c, poly));
    if (isContextContainer(doc, k) && k.type !== 'PAGE' && !all && doc.children(k.id).length) {
      out.push(...nodesInLasso(doc, k.id, poly));
      continue;
    }
    if (all || pointInPolygon({ x: b.x + b.width / 2, y: b.y + b.height / 2 }, poly)) out.push(k);
  }
  return out;
}

function groupBounds(doc: Document, g: NodeData): Rect {
  return transformRect(doc.worldTransform(g.id), { x: 0, y: 0, width: g.width, height: g.height });
}
